import GeneralLineDiagramStore from '../GeneralLineDiagramStore';
import {AnalyticsTab, DiagramMode} from '../store';
import {KPI} from '../../kpi';
import {transaction} from 'mobx';
import {
    ChannelTimeSeriesRequest as ProductChannelTimeSeriesRequest,
    getDailyProductData,
    getDailyProductDataSummary,
    getHourlyProductData,
    getHourlyProductDataSummary,
    TimeSeriesRequest as ProductTimeSeriesRequest,
} from '../../apis/ProductAPI';
import {AxiosResponse} from 'axios';
import {TimeSeriesEntry} from '../../apis/TimeSeriesEntry';

class ProductLineDiagramStore extends GeneralLineDiagramStore {
    public dependantAnalyticsTab: AnalyticsTab = AnalyticsTab.ProductView;

    public getChartApi(): (param: ProductTimeSeriesRequest) => Promise<AxiosResponse<TimeSeriesEntry[]>> {
        const {diagramMode} = this.mainStore;

        switch (diagramMode) {
            case DiagramMode.TwentyFourHourMode:
            case DiagramMode.Hourly:
                return getHourlyProductData;
            case DiagramMode.Daily:
                return getDailyProductData;
            default:
                throw Error('API for not available for ' + diagramMode);
        }
    }

    public getSummaryChartApi(): (param: ProductChannelTimeSeriesRequest) => Promise<AxiosResponse<TimeSeriesEntry[]>> {
        const {diagramMode} = this.mainStore;
        switch (diagramMode) {
            case DiagramMode.TwentyFourHourMode:
            case DiagramMode.Hourly:
                return getHourlyProductDataSummary;
            case DiagramMode.Daily:
                return getDailyProductDataSummary;
            default:
                throw Error('API for not available for ' + diagramMode);
        }
    }

    public addTermToDiagram(
        productId: string,
        kpi: KPI,
        title: string,
        channelName: string,
        colorIndex?: number,
        testId?: string,
        variantId?: string
    ) {
        /**
         * Adds a term to the diagram. The term will be requested from the backend and loaded into the diagram when present.
         * The currently selected channel will be chosen as the channel to this term.
         * @type {boolean}
         */

        const duplicateFound = this.requestedNormalDiagrams.some((data) => {
            return (
                data.searchtermOrProductId === productId &&
                data.channelName === channelName &&
                data.kpi === kpi &&
                testId === data.testId &&
                data.variantId === variantId
            );
        });

        if (!duplicateFound) {
            this.requestedNormalDiagrams.push({
                searchtermOrProductId: productId,
                channelName,
                kpi,
                testId,
                variantId,
            });

            this.updateTermColorIndexes(productId, kpi, channelName, colorIndex, testId, variantId);
            this.loadDiagramData(productId, channelName, kpi, testId, variantId);
        }
    }

    public addChannelToDiagram(channelName: string, kpi: KPI, title?: string, colorIndex?: number, testId?: string, variantId?: string) {
        /**
         * Add a channel to the diagram. The channel data gets requested from the backend.
         * @type {boolean}
         */
        const {selectedChannel} = this.channelState;
        if (!selectedChannel) {
            return;
        }

        const duplicateFound = this.requestedChannelDiagrams.some((data) => {
            return data.channelName === channelName && data.kpi === kpi && testId === data.testId && variantId === data.variantId;
        });

        if (!duplicateFound) {
            this.requestedChannelDiagrams.push({
                channelName,
                kpi,
                title,
                testId,
                variantId,
            });
            this.updateTermColorIndexes(channelName, kpi, selectedChannel.name, colorIndex, testId, variantId);
            this.loadDiagramDataForChannel(channelName, kpi, testId, variantId);
        }
    }

    public loadDiagramData(productId: string, channelName: string, kpi: KPI, testId?: string, variantId?: string) {
        this.dataVersionChecker.addDataVersionInfo(this.getCurrentDataVersionInfo());
        const fullChannel = this.channelState.getFullChannelForChannelName(channelName);
        const {mainDateRange, compareDateRange} = this.timeState;

        this.getChartApi()({
            channel: channelName,
            kpi,
            productId,
            interval: this.getDateRangeConversion()(mainDateRange),
            testId,
            variantId,
        })
            .then((resp) => {
                transaction(() => {
                    this.addToLoadedDiagramData({
                        channel: fullChannel,
                        searchtermOrProductId: productId,
                        kpi,
                        isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                        isChannelDiagram: false,
                        isCompareDiagram: false,
                        testId,
                        variantId,
                    });
                    this.addToDiagram(
                        this.enforceChannelInTimeSeriesEntries(resp.data, channelName),
                        {
                            channel: fullChannel,
                            searchtermOrProductId: productId,
                            kpi,
                            isCompareDiagram: false,
                            isChannelDiagram: false,
                            testId,
                            variantId,
                        },
                        mainDateRange
                    );
                    this.updateDiagramView();
                });
            })
            .catch(() => {
                /*no op*/
            });

        if (compareDateRange.startDate !== null && compareDateRange.endDate !== null && this.timeState.allowDateCompareRange) {
            // request the second data set with the comparison time range
            this.getChartApi()({
                channel: channelName,
                kpi,
                productId,
                interval: this.getDateRangeConversion()(compareDateRange),
                testId,
                variantId,
            })
                .then((resp) => {
                    transaction(() => {
                        this.addToLoadedDiagramData({
                            channel: fullChannel,
                            searchtermOrProductId: productId,
                            kpi,
                            isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                            isChannelDiagram: false,
                            isCompareDiagram: true,
                            testId,
                            variantId,
                        });
                        this.addToDiagram(
                            this.enforceChannelInTimeSeriesEntries(resp.data, channelName),
                            {
                                channel: fullChannel,
                                searchtermOrProductId: productId,
                                kpi,
                                isCompareDiagram: true,
                                isChannelDiagram: false,
                                testId,
                                variantId,
                            },
                            mainDateRange
                        );
                        this.updateDiagramView();
                    });
                })
                .catch(() => {
                    /*no op*/
                });
        }
    }

    public loadDiagramDataForChannel(channelName: string, kpi: KPI, testId?: string, variantId?: string) {
        this.dataVersionChecker.addDataVersionInfo(this.getCurrentDataVersionInfo());
        const fullChannel = this.channelState.getFullChannelForChannelName(channelName);
        const {mainDateRange, compareDateRange} = this.timeState;

        this.getSummaryChartApi()({
            channel: channelName,
            kpi,
            interval: this.getDateRangeConversion()(mainDateRange),
            testId,
            variantId,
        })
            .then((resp) => {
                transaction(() => {
                    this.addToLoadedDiagramData({
                        channel: fullChannel,
                        kpi,
                        searchtermOrProductId: channelName,
                        isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                        isChannelDiagram: true,
                        isCompareDiagram: false,
                        testId,
                        variantId,
                    });
                    this.addToDiagram(
                        this.enforceChannelInTimeSeriesEntries(resp.data, channelName),
                        {
                            channel: fullChannel,
                            searchtermOrProductId: channelName,
                            kpi,
                            isCompareDiagram: false,
                            isChannelDiagram: true,
                            testId,
                            variantId,
                        },
                        mainDateRange
                    );
                    this.updateDiagramView();
                });
            })
            .catch(() => {
                /*no op*/
            });

        if (compareDateRange.startDate !== null && compareDateRange.endDate !== null && this.timeState.allowDateCompareRange) {
            // request the second data set with the comparison time range
            this.getSummaryChartApi()({
                channel: channelName,
                kpi,
                interval: this.getDateRangeConversion()(compareDateRange),
                testId,
                variantId,
            })
                .then((resp) => {
                    transaction(() => {
                        this.addToLoadedDiagramData({
                            channel: fullChannel,
                            kpi,
                            isRate: this.mainStore.selectorTableStore.checkIfKpiIsRate(kpi),
                            searchtermOrProductId: channelName,
                            isChannelDiagram: true,
                            isCompareDiagram: true,
                            testId,
                            variantId,
                        });
                        this.addToDiagram(
                            this.enforceChannelInTimeSeriesEntries(resp.data, channelName),
                            {
                                channel: fullChannel,
                                searchtermOrProductId: channelName,
                                kpi,
                                isCompareDiagram: true,
                                isChannelDiagram: true,
                                testId,
                                variantId,
                            },
                            mainDateRange
                        );
                        this.updateDiagramView();
                    });
                })
                .catch(() => {
                    /*no op*/
                });
        }
    }

    protected reloadDiagramData() {
        this.requestedNormalDiagrams.forEach((data) => {
            this.loadDiagramData(data.searchtermOrProductId, data.channelName, data.kpi, data.testId, data.variantId);
        });

        this.requestedChannelDiagrams.forEach((data) => {
            this.loadDiagramDataForChannel(data.channelName, data.kpi, data.testId, data.variantId);
        });
    }
}

export default ProductLineDiagramStore;



// WEBPACK FOOTER //
// ./src/stores/product/ProductLineDiagramStore.ts